import {
  call,
  put,
  select,
  takeLatest,
  fork,
  takeEvery
} from '@redux-saga/core/effects'
import { map, prop } from 'ramda'
import { createRoutine } from 'redux-saga-routines'
import { navigate } from 'gatsby-plugin-intl'
import {
  selectMyFinishedOffersGeneralFiltersFormatted,
  selectMyOffersGeneralFiltersFormatted,
  selectMyOngoingGeneralOffers,
  selectSelectedGeneralOffers
} from 'src/features/myOffers/duck/selectors'
import OfferService from 'src/services/OfferService'
import ExchangeService from 'src/services/ExchangeService'
import TrackingService from 'src/services/TrackingService'
import {
  refreshGeneralOfferRoutine,
  makeGeneralOfferPublicRoutine
} from 'src/features/stocks/ducks/actions-general-cargo'
import { fillFormUsingTemplateGeneral } from 'src/features/offers/duck/actions'
import { normalizeToOfferTemplateGeneral } from 'src/features/offers/duck/normalizers'
import routes from 'src/utils/routes'
import { MENU_ITEMS } from 'src/features/dashboard/duck/consts'
import {
  acceptGeneralReservationRoutine,
  rejectGeneralReservationRoutine
} from 'src/features/reservations/duck/actions'
import { setSnackbarValues } from 'src/ducks/actions'
import translate from 'src/intl/translate'

export const getMyOngoingGeneralOffersRoutine = createRoutine(
  'GET_MY_ONGOING_GENERAL_OFFERS'
)
export const getMyFinishedGeneralOffersRoutine = createRoutine(
  'GET_MY_FINISHED_GENERAL_OFFERS'
)
export const setMyGeneralFiltersRoutine = createRoutine(
  'SET_MY_GENERAL_FILTERS'
)
export const setMyFinishedGeneralFiltersRoutine = createRoutine(
  'SET_MY_FINISHED_GENERAL_FILTERS'
)
export const setSelectedGeneralOfferRoutine = createRoutine(
  'SELECT_GENERAL_OFFER'
)
export const selectAllGeneralOffersRoutine = createRoutine(
  'SELECT_ALL_GENERAL_OFFERS'
)
export const clearSelectedGeneralOffersRoutine = createRoutine(
  'CLEAR_SELECTED_GENERAL_OFFERS'
)
export const deleteMultipleGeneralOffersRoutine = createRoutine(
  'DELETE_MULTIPLE_GENERAL_OFFERS'
)
export const deleteGeneralOfferRoutine = createRoutine('DELETE_GENERAL_OFFER')
export const restoreGeneralOfferRoutine = createRoutine('RESTORE_GENERAL_OFFER')
export const showOngoingGeneralOfferReservationsRoutine = createRoutine(
  'SHOW_ONGOING_GENERAL_OFFER_RESERVATIONS'
)
export const showFinishedGeneralOfferReservationsRoutine = createRoutine(
  'SHOW_FINISHED_GENERAL_OFFER_RESERVATIONS'
)
export const copyMyOfferToGeneralFormRoutine = createRoutine(
  'COPY_MY_OFFER_TO_GENERAL_FORM'
)
export const createGeneralOfferTrackingRoutine = createRoutine(
  'CREATE_GENERAL_OFFER_TRACKING'
)

export function* getMyOngoingGeneralOffers() {
  yield put(getMyOngoingGeneralOffersRoutine.request())
  try {
    const filters = yield select(selectMyOffersGeneralFiltersFormatted)
    const {
      response: { data, meta }
    } = yield call(OfferService.showMyOngoingGeneralOffers, filters)
    yield put(getMyOngoingGeneralOffersRoutine.success({ data, meta }))
  } catch (e) {
    yield put(getMyOngoingGeneralOffersRoutine.failure())
    console.log(e)
  }
}

function* getMyFinishedGeneralOffers() {
  yield put(getMyFinishedGeneralOffersRoutine.request())
  try {
    const filters = yield select(selectMyFinishedOffersGeneralFiltersFormatted)
    const {
      response: { data, meta }
    } = yield call(OfferService.showMyFinishedGeneralOffers, filters)
    yield put(getMyFinishedGeneralOffersRoutine.success({ data, meta }))
  } catch (e) {
    yield put(getMyFinishedGeneralOffersRoutine.failure())
    console.log(e)
  }
}

function* showOngoingGeneralOfferReservations({ payload }) {
  yield put(showOngoingGeneralOfferReservationsRoutine.request())
  try {
    const { data } = yield call(
      OfferService.showOngoingGeneralOfferReservations,
      payload.id
    )
    yield put(showOngoingGeneralOfferReservationsRoutine.success(data))
  } catch (e) {
    yield put(showOngoingGeneralOfferReservationsRoutine.failure())
    console.log(e)
  }
}

function* showFinishedGeneralOfferReservations({ payload }) {
  yield put(showFinishedGeneralOfferReservationsRoutine.request())
  try {
    const { data } = yield call(
      OfferService.showFinishedGeneralOfferReservations,
      payload.id
    )
    yield put(showFinishedGeneralOfferReservationsRoutine.success(data))
  } catch (e) {
    yield put(showFinishedGeneralOfferReservationsRoutine.failure())
    console.log(e)
  }
}

function* setMyGeneralFilters({ payload }) {
  yield put(setMyGeneralFiltersRoutine.success(payload))
}

function* setMyFinishedGeneralFilters({ payload }) {
  yield put(setMyFinishedGeneralFiltersRoutine.success(payload))
}

function* setSelectedOffer({ payload }) {
  yield put(setSelectedGeneralOfferRoutine.success(payload))
}

function* selectAllOffers() {
  const offers = yield select(selectMyOngoingGeneralOffers)
  const ids = map(prop('id'), offers)
  yield put(selectAllGeneralOffersRoutine.success({ ids }))
}

function* clearSelectedOffers() {
  yield put(clearSelectedGeneralOffersRoutine.success())
}

function* deleteMultipleGeneralOffers() {
  yield put(deleteMultipleGeneralOffersRoutine.request())
  try {
    const ids = yield select(selectSelectedGeneralOffers)
    yield call(OfferService.deleteMultipleGeneralOffers, { ids })
    yield put(deleteMultipleGeneralOffersRoutine.success())
    yield fork(setSnackbarValues, {
      payload: {
        message: translate().formatMessage({
          id: 'myOffers.deleteOffersSuccessMessage'
        })
      }
    })
  } catch (e) {
    yield put(deleteMultipleGeneralOffersRoutine.failure())
    yield fork(setSnackbarValues, {
      payload: {
        message: translate().formatMessage({
          id: 'common.errorMessage'
        }),
        type: 'alert'
      }
    })
    console.log(e)
  }
}

function* deleteGeneralOffer({ payload }) {
  yield put(deleteGeneralOfferRoutine.request())
  try {
    yield call(OfferService.deleteGeneralOffer, payload.id, {
      reason: payload.reason
    })
    yield put(deleteGeneralOfferRoutine.success())
    yield fork(setSnackbarValues, {
      payload: {
        message: translate().formatMessage({
          id: 'myOffers.deleteOneOfferSuccessMessage'
        })
      }
    })
  } catch (e) {
    yield put(deleteGeneralOfferRoutine.failure())
    yield fork(setSnackbarValues, {
      payload: {
        message: translate().formatMessage({
          id: 'common.errorMessage'
        }),
        type: 'alert'
      }
    })
    console.log(e)
  }
}

function* restoreGeneralOffer({ payload }) {
  yield put(restoreGeneralOfferRoutine.request())
  try {
    yield call(OfferService.restoreGeneralOffer, payload.id)
    yield put(restoreGeneralOfferRoutine.success())
    yield fork(setSnackbarValues, {
      payload: {
        message: translate().formatMessage({
          id: 'myOffers.restoreOfferSuccessMessage'
        })
      }
    })
  } catch (e) {
    yield put(restoreGeneralOfferRoutine.failure())
    console.log(e)
  }
}

function* copyMyOfferToGeneralForm({ payload }) {
  const { data } = yield call(ExchangeService.showGeneralOffer, {
    id: payload.offer.id
  })
  const normalized = normalizeToOfferTemplateGeneral(data)

  yield call(fillFormUsingTemplateGeneral, {
    payload: { template: normalized }
  })
  navigate(`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.OFFER_FORM_GENERAL}`)
}

export function* createGeneralOfferTracking({ payload }) {
  yield put(createGeneralOfferTrackingRoutine.request())
  try {
    const { data } = yield call(
      TrackingService.createGeneralOfferTracking,
      payload.id
    )
    yield put(createGeneralOfferTrackingRoutine.success(data))
  } catch (e) {
    yield put(createGeneralOfferTrackingRoutine.fulfill())
  }
}

function* getMyOngoingGeneralOffersRoutineWatcher() {
  yield takeLatest(
    [
      setMyGeneralFiltersRoutine.SUCCESS,
      getMyOngoingGeneralOffersRoutine.TRIGGER,
      deleteMultipleGeneralOffersRoutine.SUCCESS,
      deleteGeneralOfferRoutine.SUCCESS,
      restoreGeneralOfferRoutine.SUCCESS,
      refreshGeneralOfferRoutine.SUCCESS,
      makeGeneralOfferPublicRoutine.SUCCESS,
      rejectGeneralReservationRoutine.SUCCESS
    ],
    getMyOngoingGeneralOffers
  )
}

function* getMyFinishedGeneralOffersRoutineWatcher() {
  yield takeLatest(
    [
      getMyFinishedGeneralOffersRoutine.TRIGGER,
      setMyFinishedGeneralFiltersRoutine.SUCCESS,
      acceptGeneralReservationRoutine.SUCCESS,
      rejectGeneralReservationRoutine.SUCCESS,
      restoreGeneralOfferRoutine.SUCCESS,
      deleteGeneralOfferRoutine.SUCCESS
    ],
    getMyFinishedGeneralOffers
  )
}

function* setMyGeneralFiltersRoutineWatcher() {
  yield takeLatest(setMyGeneralFiltersRoutine.TRIGGER, setMyGeneralFilters)
}

function* setMyFinishedGeneralFiltersRoutineWatcher() {
  yield takeLatest(
    setMyFinishedGeneralFiltersRoutine.TRIGGER,
    setMyFinishedGeneralFilters
  )
}

function* setSelectedOfferRoutineWatcher() {
  yield takeLatest(setSelectedGeneralOfferRoutine.TRIGGER, setSelectedOffer)
}

function* selectAllOffersRoutineWatcher() {
  yield takeLatest(selectAllGeneralOffersRoutine.TRIGGER, selectAllOffers)
}
function* clearSelectedOffersRoutineWatcher() {
  yield takeLatest(
    [
      getMyOngoingGeneralOffersRoutine.SUCCESS,
      clearSelectedGeneralOffersRoutine.TRIGGER
    ],
    clearSelectedOffers
  )
}

function* deleteMultipleGeneralOffersRoutineWatcher() {
  yield takeLatest(
    deleteMultipleGeneralOffersRoutine.TRIGGER,
    deleteMultipleGeneralOffers
  )
}

function* deleteGeneralOfferRoutineWatcher() {
  yield takeLatest(deleteGeneralOfferRoutine.TRIGGER, deleteGeneralOffer)
}

function* restoreGeneralOfferRoutineWatcher() {
  yield takeLatest(restoreGeneralOfferRoutine.TRIGGER, restoreGeneralOffer)
}

function* showOngoingGeneralOfferReservationsRoutineWatcher() {
  yield takeEvery(
    showOngoingGeneralOfferReservationsRoutine.TRIGGER,
    showOngoingGeneralOfferReservations
  )
}

function* showFinishedGeneralOfferReservationsRoutineWatcher() {
  yield takeEvery(
    showFinishedGeneralOfferReservationsRoutine.TRIGGER,
    showFinishedGeneralOfferReservations
  )
}

function* copyMyOfferToGeneralFormRoutineWatcher() {
  yield takeLatest(
    copyMyOfferToGeneralFormRoutine.TRIGGER,
    copyMyOfferToGeneralForm
  )
}

function* createGeneralOfferTrackingRoutineWatcher() {
  yield takeLatest(
    createGeneralOfferTrackingRoutine.TRIGGER,
    createGeneralOfferTracking
  )
}

export const myGeneralOffersSagas = [
  fork(getMyOngoingGeneralOffersRoutineWatcher),
  fork(setMyGeneralFiltersRoutineWatcher),
  fork(setSelectedOfferRoutineWatcher),
  fork(deleteMultipleGeneralOffersRoutineWatcher),
  fork(selectAllOffersRoutineWatcher),
  fork(clearSelectedOffersRoutineWatcher),
  fork(deleteGeneralOfferRoutineWatcher),
  fork(getMyFinishedGeneralOffersRoutineWatcher),
  fork(setMyFinishedGeneralFiltersRoutineWatcher),
  fork(restoreGeneralOfferRoutineWatcher),
  fork(showOngoingGeneralOfferReservationsRoutineWatcher),
  fork(showFinishedGeneralOfferReservationsRoutineWatcher),
  fork(copyMyOfferToGeneralFormRoutineWatcher),
  fork(createGeneralOfferTrackingRoutineWatcher)
]
